import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';

import {
  makeStyles,
  Typography,
  Card,
  Box,
  Container,
  Link,
  Button
} from '@material-ui/core';

import Page from 'src/shared/ui/components/Page';
import Form from 'src/shared/ui/components/Form';
import processFormErrorMessages from 'src/shared/ui/components/Form/FormErrorMessage';

import validateSchema from 'src/shared/models/SchemaValidator';
import ContractType from 'src/contract-type/models/ContractType';

import createContractType from 'src/contract-type/use-cases/createContractType';
import updateContractTypeById from 'src/contract-type/use-cases/updateContractTypeById';
import getContractTypeList from 'src/contract-type/use-cases/getContractTypeList';
import getContractTypeById from 'src/contract-type/use-cases/getContractTypeById';

import getCompanyServiceList from 'src/company-service/use-cases/getCompanyServiceList';
import getCompanyProductList from 'src/company-product/use-cases/getCompanyProductList';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  saveButton: {
    top: '-20px'
  },
  backButton: {
    top: '-20px',
    marginRight: theme.spacing(1)
  },
  scrollBox: {
    overflow: 'scroll',
    height: '80vh'
  }
}));

const ContractFormView = () => {
  const companyHeadquarterId = localStorage.selectedUserHeadquarter;
  const classes = useStyles();
  const [formFields, setFormFields] = useState([]);
  const [isRequesting, setIsRequesting] = useState(false);
  const { contractTypeId } = useParams();
  const navigate = useNavigate();

  const initFormFields = async (fields) => {
    const contractTypeList = await getContractTypeList({
      companyHeadquarterId,
      page: null,
      limit: null,
      orderBy: ''
    });
    const companyServiceList = await getCompanyServiceList({
      page: null,
      limit: null
    });
    const companyProductList = await getCompanyProductList({
      page: null,
      limit: null
    });

    const contractType = ContractType({
      fields,
      contractTypeList: contractTypeList.rows,
      companyServiceList,
      companyProductList,
      userHeadquarterList: JSON.parse(localStorage.userHeadquarterList),
      selectedUserHeadquarter: localStorage.selectedUserHeadquarter
    });

    setFormFields(contractType);
  };

  const fetchForm = async () => {
    const contractType = await getContractTypeById(contractTypeId);

    initFormFields(contractType);
  };

  useEffect(() => {
    if (contractTypeId === 'new') {
      initFormFields({
        totalDuration: 24
      });
    } else {
      fetchForm();
    }
  }, []);

  const validateFormFields = async (formFieldsValues) => {
    const validatedFormFields = await validateSchema(formFieldsValues);
    formFieldsValues = processFormErrorMessages(
      validatedFormFields,
      formFieldsValues
    );
    setFormFields(formFieldsValues.fields);

    return {
      errors: formFieldsValues.errors,
      fields: validatedFormFields
    };
  };

  const handleChangeFormFieldsValues = (formFieldsValues) => {
    setFormFields(formFieldsValues);
  };

  const postForm = async () => {
    if (isRequesting) {
      return;
    }

    const validatedForm = await validateFormFields(formFields);
    if (validatedForm.errors) {
      alert('Rellene todos los campos correctamente.');
      return;
    }

    setIsRequesting(true);

    let result;
    if (contractTypeId === 'new') {
      result = await createContractType(validatedForm.fields);
    } else {
      result = await updateContractTypeById(
        validatedForm.fields,
        contractTypeId
      );
    }

    setIsRequesting(false);

    if (typeof result.id === 'undefined') {
      alert('Se ha producido un error, vuelva a intentarlo');
    } else {
      navigate('/app/contract-types');
    }
  };

  return (
    <Page className={classes.root} title="Tipos de contratos">
      <Container maxWidth={false}>
        <Box className={classes.navigation} display="flex">
          <Typography color="textSecondary" variant="body2">
            <Link component={RouterLink} to="/app/contract-types">
              Tipos de contratos
            </Link>
            {` >  ${
              formFields.length && formFields[1].value.length
                ? formFields[1].value
                : 'Nuevo'
            }`}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <Link component={RouterLink} to="/app/contract-types">
            <Button className={classes.backButton}>{'Volver a listado'}</Button>
          </Link>
          <Button
            color="primary"
            variant="contained"
            className={classes.saveButton}
            onClick={postForm}
          >
            Guardar
          </Button>
        </Box>
        <Card>
          <Form
            fields={formFields}
            onChangeFields={handleChangeFormFieldsValues}
          />
        </Card>
      </Container>
    </Page>
  );
};

export default ContractFormView;
